import { useEditor, EditorContent, useEditorState, Extension } from "@tiptap/react"
import StarterKit from "@tiptap/starter-kit"
import Image from "@tiptap/extension-image"
import Link from "@tiptap/extension-link"
import { TextStyle, FontSize, Color } from "@tiptap/extension-text-style"
import styles from "./editor.module.css"
import data from "@emoji-mart/data"
import Picker from "@emoji-mart/react"
import GifPicker from "./GifPicker"
import { useState, useEffect, useRef } from "react"

// TextAlign customizado — sem dependência de pacote externo
const TextAlign = Extension.create({
    name: "textAlign",
    addOptions() {
        return { types: ["heading", "paragraph"], defaultAlignment: "left" }
    },
    addGlobalAttributes() {
        return [{
            types: this.options.types,
            attributes: {
                textAlign: {
                    default: this.options.defaultAlignment,
                    parseHTML: el => el.style.textAlign || this.options.defaultAlignment,
                    renderHTML: ({ textAlign }) =>
                        textAlign && textAlign !== this.options.defaultAlignment
                            ? { style: `text-align: ${textAlign}` }
                            : {},
                },
            },
        }]
    },
    addCommands() {
        return {
            setTextAlign: alignment => ({ commands }) =>
                this.options.types.every(type =>
                    commands.updateAttributes(type, { textAlign: alignment })
                ),
        }
    },
})

const FONT_SIZES = ["12px", "14px", "16px", "18px", "20px", "24px", "28px", "32px"]

export default function Editor({ value, onChange }) {
    const [showEmoji, setShowEmoji] = useState(false)
    const [showGif, setShowGif] = useState(false)
    const [fontSize, setFontSize] = useState("16px")
    const [color, setColor] = useState("#1e293b")
    const emojiRef = useRef(null)

    useEffect(() => {
        function onOutside(e) {
            if (emojiRef.current && !emojiRef.current.contains(e.target)) setShowEmoji(false)
        }
        if (showEmoji) document.addEventListener("mousedown", onOutside)
        return () => document.removeEventListener("mousedown", onOutside)
    }, [showEmoji])

    const editor = useEditor({
        extensions: [
            StarterKit,
            Image,
            Link.configure({ openOnClick: false }),
            TextStyle,
            FontSize,
            Color,
            TextAlign,
        ],
        content: value,
        onUpdate({ editor }) {
            onChange(editor.getHTML())
        },
    })

    const state = useEditorState({
        editor,
        selector: ({ editor }) => ({
            bold:        editor.isActive("bold"),
            italic:      editor.isActive("italic"),
            bulletList:  editor.isActive("bulletList"),
            heading:     editor.isActive("heading", { level: 2 }),
            alignLeft:   editor.isActive("paragraph", { textAlign: "left" }) || editor.isActive("heading", { textAlign: "left" }),
            alignCenter: editor.isActive("paragraph", { textAlign: "center" }) || editor.isActive("heading", { textAlign: "center" }),
            alignRight:  editor.isActive("paragraph", { textAlign: "right" }) || editor.isActive("heading", { textAlign: "right" }),
        }),
    })

    useEffect(() => {
        if (!editor) return
        if (value === editor.getHTML()) return
        editor.commands.setContent(value || "")
    }, [value, editor])

    if (!editor) return null

    function applyFontSize(size) {
        setFontSize(size)
        editor.chain().focus().setFontSize(size).run()
    }

    function applyColor(c) {
        setColor(c)
        editor.chain().focus().setColor(c).run()
    }

    const handleImageUpload = (event) => {
        const file = event.target.files[0]
        if (!file) return
        const reader = new FileReader()
        reader.onload = (e) => {
            editor.chain().focus().setImage({ src: e.target.result }).run()
        }
        reader.readAsDataURL(file)
    }

    return (
        <div className={styles.editorContainer}>
            <div className={styles.toolbar}>

                <button className={state.bold ? styles.active : ""}
                    onClick={() => editor.chain().focus().toggleBold().run()}>B</button>
                <button className={state.italic ? styles.active : ""}
                    onClick={() => editor.chain().focus().toggleItalic().run()}>I</button>
                <button className={state.heading ? styles.active : ""}
                    onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}>H2</button>
                <button className={state.bulletList ? styles.active : ""}
                    onClick={() => editor.chain().focus().toggleBulletList().run()}>• Lista</button>

                <div className={styles.sep} />

                <button className={state.alignLeft ? styles.active : ""}
                    onClick={() => editor.chain().focus().setTextAlign("left").run()} title="Esquerda">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="15" y2="12" /><line x1="3" y1="18" x2="18" y2="18" />
                    </svg>
                </button>
                <button className={state.alignCenter ? styles.active : ""}
                    onClick={() => editor.chain().focus().setTextAlign("center").run()} title="Centralizar">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="3" y1="6" x2="21" y2="6" /><line x1="6" y1="12" x2="18" y2="12" /><line x1="4" y1="18" x2="20" y2="18" />
                    </svg>
                </button>
                <button className={state.alignRight ? styles.active : ""}
                    onClick={() => editor.chain().focus().setTextAlign("right").run()} title="Direita">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="3" y1="6" x2="21" y2="6" /><line x1="9" y1="12" x2="21" y2="12" /><line x1="6" y1="18" x2="21" y2="18" />
                    </svg>
                </button>

                <div className={styles.sep} />

                <select className={styles.sizeSelect} value={fontSize}
                    onChange={e => applyFontSize(e.target.value)} title="Tamanho da fonte">
                    {FONT_SIZES.map(s => (
                        <option key={s} value={s}>{s.replace("px", "")}px</option>
                    ))}
                </select>

                <label className={styles.colorLabel} title="Cor da fonte">
                    <span className={styles.colorA} style={{ borderBottom: `3px solid ${color}` }}>A</span>
                    <input type="color" value={color}
                        onChange={e => applyColor(e.target.value)}
                        className={styles.colorInput} />
                </label>

                <div className={styles.sep} />

                <button onClick={() => {
                    const url = prompt("URL do link")
                    if (url) editor.chain().focus().setLink({ href: url }).run()
                }}>🔗</button>

                <label className={styles.uploadBtn}>
                    🖼
                    <input type="file" hidden onChange={handleImageUpload} />
                </label>

                <div className={styles.emojiWrap} ref={emojiRef}>
                    <button onClick={() => setShowEmoji(o => !o)}>🙂</button>
                    {showEmoji && (
                        <div className={styles.emojiPicker}>
                            <Picker data={data} locale="pt"
                                onEmojiSelect={em => {
                                    editor.chain().focus().insertContent(em.native).run()
                                    setShowEmoji(false)
                                }} />
                        </div>
                    )}
                </div>

                <button
                    onClick={() => setShowGif(true)}
                    title="Inserir GIF"
                    className={styles.gifBtn}
                >
                    GIF
                </button>

            </div>

            <EditorContent editor={editor} className={styles.editor} />

            {showGif && (
                <GifPicker
                    onSelect={url => editor.chain().focus().setImage({ src: url }).run()}
                    onClose={() => setShowGif(false)}
                />
            )}
        </div>
    )
}
